import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Interface representing the return value of useScrollFade.
 * @interface IUseScrollFade
 * @property {React.RefObject<HTMLDivElement>} scrollRef: Ref to attach to the scroll container.
 * @property {boolean} showTopFade: Whether content is hidden above the viewport.
 * @property {boolean} showBottomFade: Whether content is hidden below the viewport.
 * @property {Function} onScroll: Scroll handler to attach to the container.
 */
export interface IUseScrollFade {
  scrollRef: React.RefObject<HTMLDivElement>;
  showTopFade: boolean;
  showBottomFade: boolean;
  onScroll: () => void;
}

const useScrollFade = (deps: Array<unknown> = []): IUseScrollFade => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setShowTopFade(el.scrollTop > 0);
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    update();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [update, ...deps]);

  return { scrollRef, showTopFade, showBottomFade, onScroll: update };
};

export default useScrollFade;
